// Enums
export enum CategorieClient {
  PERSONNE_PHYSIQUE = 'PERSONNE_PHYSIQUE',
  PERSONNE_MORALE = 'PERSONNE_MORALE',
  FORFAITAIRE = 'FORFAITAIRE',
  EXONERE = 'EXONERE',
  EXPORT = 'EXPORT',
}

export enum TauxTva {
  TVA_0 = 'TVA_0',
  TVA_7 = 'TVA_7',
  TVA_13 = 'TVA_13',
  TVA_19 = 'TVA_19',
}

export enum Unite {
  PIECE = 'PIECE',
  KG = 'KG',
  LITRE = 'LITRE',
  METRE = 'METRE',
  M2 = 'M2',
  M3 = 'M3',
  HEURE = 'HEURE',
  JOUR = 'JOUR',
  FORFAIT = 'FORFAIT',
  SERVICE = 'SERVICE',
}

// Empreinte carbone (intégration Green Target / GTS) — alignées sur les enums backend.
export enum EmissionScope {
  SCOPE_1 = 'SCOPE_1',
  SCOPE_2 = 'SCOPE_2',
  SCOPE_3 = 'SCOPE_3',
}

export enum EmissionCategory {
  ENERGY = 'ENERGY',
  TRANSPORT = 'TRANSPORT',
  WASTE = 'WASTE',
  WATER = 'WATER',
  MATERIALS = 'MATERIALS',
  DIGITAL = 'DIGITAL',
  OTHER = 'OTHER',
}

export const EMISSION_SCOPE_LABELS: Record<EmissionScope, string> = {
  [EmissionScope.SCOPE_1]: 'Scope 1 — Émissions directes',
  [EmissionScope.SCOPE_2]: 'Scope 2 — Énergie achetée',
  [EmissionScope.SCOPE_3]: 'Scope 3 — Chaîne de valeur',
};

export const EMISSION_CATEGORY_LABELS: Record<EmissionCategory, string> = {
  [EmissionCategory.ENERGY]: 'Énergie',
  [EmissionCategory.TRANSPORT]: 'Transport',
  [EmissionCategory.WASTE]: 'Déchets',
  [EmissionCategory.WATER]: 'Eau',
  [EmissionCategory.MATERIALS]: 'Matériaux',
  [EmissionCategory.DIGITAL]: 'Numérique',
  [EmissionCategory.OTHER]: 'Autre',
};

export enum TypeDocument {
  FACTURE = 'FACTURE',
  DEVIS = 'DEVIS',
  AVOIR = 'AVOIR',
  COMMANDE = 'COMMANDE',
  BON_LIVRAISON = 'BON_LIVRAISON',
  BON_COMMANDE = 'BON_COMMANDE',
}

export enum ModePaiement {
  ESPECES = 'ESPECES',
  CHEQUE = 'CHEQUE',
  VIREMENT = 'VIREMENT',
  TRAITE = 'TRAITE',
  CARTE_BANCAIRE = 'CARTE_BANCAIRE',
  PRELEVEMENT = 'PRELEVEMENT',
}

export enum StatutPaiement {
  PAYEE = 'PAYEE',
  PARTIELLEMENT_PAYEE = 'PARTIELLEMENT_PAYEE',
  EN_RETARD = 'EN_RETARD',
  NON_PAYEE = 'NON_PAYEE',
}

/** Statut de cycle de vie métier d'un document (TF-001), distinct du paiement. */
export enum StatutDocument {
  BROUILLON = 'BROUILLON',
  ENVOYE = 'ENVOYE',
  ACCEPTE = 'ACCEPTE',
  REFUSE = 'REFUSE',
  EXPIRE = 'EXPIRE',
  VALIDE = 'VALIDE',
  ANNULE = 'ANNULE',
}

/** Étape du pipeline commercial (TF-005). */
export enum EtapeOpportunite {
  PROSPECTION = 'PROSPECTION',
  QUALIFICATION = 'QUALIFICATION',
  PROPOSITION = 'PROPOSITION',
  NEGOCIATION = 'NEGOCIATION',
  GAGNEE = 'GAGNEE',
  PERDUE = 'PERDUE',
}

// Interfaces
export interface Company {
  id: number;
  raisonSociale: string;
  matriculeFiscal: string;
  adresse: string;
  codePostal: string;
  ville: string;
  telephone: string;
  email: string;
  siteWeb?: string;
  rib: string;
  banque: string;
  logoUrl?: string;
  timbreFiscal: number;
  tauxFodec: number;
  einvoicingEnabled?: boolean;
  ttnIdentifiant?: string;
  ttnMotDePasse?: string;
  ttnIice?: string;
  ttnTestMode?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export type TypeRelation = 'CLIENT' | 'FOURNISSEUR' | 'MIXTE';

export interface Client {
  id: number;
  code: string;
  raisonSociale: string;
  typeRelation?: TypeRelation;
  matriculeFiscal?: string;
  categorie: CategorieClient;
  adresse: string;
  codePostal?: string;
  ville: string;
  telephone?: string;
  mobile?: string;
  email?: string;
  fax?: string;
  contactNom?: string;
  rib?: string;
  banque?: string;
  plafondCredit?: number;
  delaiPaiement?: number;
  exonere: boolean;
  tauxRetenueSource?: number;
  notes?: string;
  actif: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface Fournisseur {
  id: number;
  raisonSociale: string;
  matriculeFiscal?: string;
  adresse?: string;
  codePostal?: string;
  ville?: string;
  telephone?: string;
  email?: string;
  contactNom?: string;
  rib?: string;
  banque?: string;
  delaiPaiement?: number;
  tauxRetenueSource?: number;
  actif: boolean;
  /** Note qualité 0-5 (TF-020). */
  noteQualite?: number;
  /** CO₂ transport estimé par commande, kg (TF-028). */
  transportCo2ParCommande?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface NumberingConfig {
  id: number;
  typeDocument: TypeDocument;
  prefix: string;
  format: string;
  startAt: number;
  resetYearly: boolean;
  currentNumber: number;
  nextPreview: string;
}

export interface InvoiceTemplate {
  id: number;
  name: string;
  isDefault: boolean;
  primaryColor: string;
  footerText?: string;
  legalMentions?: string;
  paymentTerms?: string;
  /** Show the payment / RIB QR code on documents using this template. */
  showQrCode?: boolean;
  /** JSON string of the drag-and-drop layout — see TemplateLayout for parsed shape. */
  layout?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type TemplateElementId = 'logo' | 'company' | 'title' | 'client';

export interface TemplateBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface TemplateLayout {
  version: 1;
  elements: Partial<Record<TemplateElementId, TemplateBox>>;
}

export interface Article {
  id: number;
  code: string;
  designation: string;
  description?: string;
  prixUnitaireHT: number;
  tauxTva: TauxTva;
  unite: Unite;
  fodec: boolean;
  categorie?: string;
  reference?: string;
  codeBarres?: string;
  stockActuel?: number;
  stockMinimum?: number;
  /** Délai de réapprovisionnement fournisseur en jours (lead time). */
  delaiApprovisionnement?: number;
  /** Coût moyen unitaire pondéré (CMUP/PMP) — maintenu par le backend, lecture seule. */
  cmup?: number;
  actif: boolean;
  /** Facteur d'émission en kg CO2e par unité (intégration Green Target / GTS). */
  emissionFactor?: number | null;
  emissionScope?: EmissionScope | null;
  emissionCategory?: EmissionCategory | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface LigneDocument {
  id?: number;
  articleId: number;
  articleCode?: string;
  articleDesignation?: string;
  quantite: number;
  prixUnitaireHT: number;
  tauxTva: TauxTva;
  remise: number;
  fodec: boolean;
  montantHT: number;
  montantTva: number;
  montantTTC: number;
  montantFodec: number;
  ordre: number;
  /** Empreinte carbone de la ligne en kg CO₂e (null si pas de facteur d'émission). */
  co2eKg?: number | null;
  /** Reliquats (TF-003) — sur les lignes de COMMANDE. */
  quantiteLivree?: number;
  quantiteFacturee?: number;
  reliquatLivraison?: number;
  reliquatFacturation?: number;
}

export type SensDocument = 'VENTE' | 'ACHAT';

export interface Document {
  id: number;
  numero: string;
  typeDocument: TypeDocument;
  sensDocument?: SensDocument;
  /** Numéro d'origine chez le fournisseur (factures d'ACHAT). */
  numeroFournisseur?: string;
  dateDocument: string;
  dateEcheance?: string;
  clientId: number;
  clientRaisonSociale?: string;
  clientMatriculeFiscal?: string;
  /** Tiers fournisseur (documents d'ACHAT). */
  fournisseurId?: number;
  fournisseurRaisonSociale?: string;
  fournisseurMatriculeFiscal?: string;
  lignes: LigneDocument[];
  totalRemise?: number;
  totalHT: number;
  totalTva: number;
  totalFodec: number;
  timbreFiscal: number;
  /**
   * Applique le droit de timbre fiscal à ce document. Vrai par défaut ; décoché pour les
   * opérations exonérées (export, client non résident, franchise…), le timbre n'est alors
   * ni calculé ni imprimé sur le PDF.
   */
  appliquerTimbreFiscal?: boolean;
  totalTTC: number;
  retenueSource: number;
  netAPayer: number;
  modePaiement: ModePaiement;
  statutPaiement: StatutPaiement;
  /** Statut de cycle de vie métier (TF-001). */
  statut?: StatutDocument;
  /** Transitions de statut autorisées depuis l'état courant. */
  statutsCibles?: StatutDocument[];
  montantPaye: number;
  resteAPayer: number;
  notes?: string;
  referenceExterne?: string;
  documentSourceId?: number;
  documentSourceNumero?: string;
  actif: boolean;
  factureElectronique?: boolean;
  statutFactureElectronique?: StatutFactureElectronique;
  templateId?: number;
  templateName?: string;
  referenceTtn?: string;
  dateTransmissionTtn?: string;
  messageTtn?: string;
  createdAt?: string;
  updatedAt?: string;
  /** Empreinte carbone totale du document en kg CO₂e (somme des lignes). */
  co2eKg?: number | null;
}

export type StatutFactureElectronique =
  | 'BROUILLON'
  | 'GENEREE'
  | 'TRANSMISE'
  | 'ACCEPTEE'
  | 'REJETEE';

export interface FactureElectroniqueStatus {
  documentId: number;
  numero: string;
  statutFactureElectronique?: StatutFactureElectronique;
  referenceTtn?: string;
  dateTransmissionTtn?: string;
  messageTtn?: string;
  teifXml?: string;
}

export interface Paiement {
  id: number;
  documentId: number;
  documentNumero?: string;
  clientRaisonSociale?: string;
  datePaiement: string;
  montant: number;
  modePaiement: ModePaiement;
  reference?: string;
  banque?: string;
  dateEcheance?: string;
  notes?: string;
  createdAt?: string;
}

export interface Echeance {
  id: number;
  ordre: number;
  libelle?: string;
  dateEcheance: string;
  montant: number;
  montantPaye: number;
  reste: number;
  statut: StatutPaiement;
  enRetard: boolean;
}

export interface Echeancier {
  documentId: number;
  netAPayer: number;
  totalEcheances: number;
  totalPaye: number;
  resteGlobal: number;
  prochaineEcheance?: string;
  enRetard: boolean;
  echeances: Echeance[];
}

export interface Utilisateur {
  id: number;
  nom: string;
  prenom: string;
  email: string;
  role: string;
  actif: boolean;
  derniereConnexion?: string;
  createdAt?: string;
}

export interface DashboardStats {
  caMensuel: number;
  caAnnuel: number;
  facturesImpayeesCount: number;
  facturesImpayeesMontant: number;
  nombreClients: number;
  caMensuelParMois: MonthlyStat[];
  topClients: TopClient[];
  dernieresFactures: Document[];
}

export interface MonthlyStat {
  mois: string;
  montant: number;
}

export interface TopClient {
  clientId: number;
  raisonSociale: string;
  totalCA: number;
  nombreFactures: number;
}

// Pagination
export interface Page<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

// Search/filter params
export interface ClientSearchParams {
  search?: string;
  categorie?: CategorieClient;
  actif?: boolean;
  page?: number;
  size?: number;
  sort?: string;
}

export interface ArticleSearchParams {
  search?: string;
  categorie?: string;
  tauxTva?: TauxTva;
  actif?: boolean;
  page?: number;
  size?: number;
  sort?: string;
}

export interface DocumentSearchParams {
  typeDocument?: TypeDocument;
  statutPaiement?: StatutPaiement;
  clientId?: number;
  dateDebut?: string;
  dateFin?: string;
  search?: string;
  page?: number;
  size?: number;
  sort?: string;
}

export interface PaiementSearchParams {
  documentId?: number;
  clientId?: number;
  dateDebut?: string;
  dateFin?: string;
  modePaiement?: ModePaiement;
  page?: number;
  size?: number;
  sort?: string;
}

export interface RapportTVAData {
  periode: string;
  tva7Collectee: number;
  tva13Collectee: number;
  tva19Collectee: number;
  totalTvaCollectee: number;
  baseHT7: number;
  baseHT13: number;
  baseHT19: number;
  totalBaseHT: number;
  fodecTotal: number;
  timbreTotal: number;
  tvaDeductible: number;
  tvaNetteAPayer: number;
}

/** Reporting de marge (TF-011). */
export interface MargeLigne {
  id: number;
  libelle: string;
  quantite: number;
  revenuHT: number;
  coutHT: number;
  marge: number;
  tauxMargePct: number | null;
  coutIncomplet: boolean;
}

export interface MargeRapport {
  groupBy: 'produit' | 'client';
  lignes: MargeLigne[];
  totalRevenuHT: number;
  totalCoutHT: number;
  totalMarge: number;
  tauxMargePct: number | null;
}

export interface EtatAnnuelData {
  annee: number;
  caTotal: number;
  caMensuel: MonthlyStat[];
  tvaCollectee: number;
  nombreFactures: number;
  nombreDevis: number;
  nombreAvoirs: number;
  topClients: TopClient[];
  repartitionTva: { tauxTva: string; montant: number }[];
}

// Helper types
export const TVA_RATES: Record<TauxTva, number> = {
  [TauxTva.TVA_0]: 0,
  [TauxTva.TVA_7]: 7,
  [TauxTva.TVA_13]: 13,
  [TauxTva.TVA_19]: 19,
};

export const TVA_LABELS: Record<TauxTva, string> = {
  [TauxTva.TVA_0]: '0%',
  [TauxTva.TVA_7]: '7%',
  [TauxTva.TVA_13]: '13%',
  [TauxTva.TVA_19]: '19%',
};

export const UNITE_LABELS: Record<Unite, string> = {
  [Unite.PIECE]: 'Pièce',
  [Unite.KG]: 'Kg',
  [Unite.LITRE]: 'Litre',
  [Unite.METRE]: 'Mètre',
  [Unite.M2]: 'm²',
  [Unite.M3]: 'm³',
  [Unite.HEURE]: 'Heure',
  [Unite.JOUR]: 'Jour',
  [Unite.FORFAIT]: 'Forfait',
  [Unite.SERVICE]: 'Service',
};

export const CATEGORIE_CLIENT_LABELS: Record<CategorieClient, string> = {
  [CategorieClient.PERSONNE_PHYSIQUE]: 'Personne physique',
  [CategorieClient.PERSONNE_MORALE]: 'Personne morale',
  [CategorieClient.FORFAITAIRE]: 'Forfaitaire',
  [CategorieClient.EXONERE]: 'Exonéré',
  [CategorieClient.EXPORT]: 'Export',
};

export const TYPE_DOCUMENT_LABELS: Record<TypeDocument, string> = {
  [TypeDocument.FACTURE]: 'Facture',
  [TypeDocument.DEVIS]: 'Devis',
  [TypeDocument.AVOIR]: 'Avoir',
  [TypeDocument.COMMANDE]: 'Commande',
  [TypeDocument.BON_LIVRAISON]: 'Bon de livraison',
  [TypeDocument.BON_COMMANDE]: 'Bon de commande',
};

export const MODE_PAIEMENT_LABELS: Record<ModePaiement, string> = {
  [ModePaiement.ESPECES]: 'Espèces',
  [ModePaiement.CHEQUE]: 'Chèque',
  [ModePaiement.VIREMENT]: 'Virement',
  [ModePaiement.TRAITE]: 'Traite',
  [ModePaiement.CARTE_BANCAIRE]: 'Carte bancaire',
  [ModePaiement.PRELEVEMENT]: 'Prélèvement',
};

export const STATUT_PAIEMENT_LABELS: Record<StatutPaiement, string> = {
  [StatutPaiement.PAYEE]: 'Payée',
  [StatutPaiement.PARTIELLEMENT_PAYEE]: 'Partiellement payée',
  [StatutPaiement.EN_RETARD]: 'En retard',
  [StatutPaiement.NON_PAYEE]: 'Non payée',
};

export const ETAPE_OPPORTUNITE_LABELS: Record<EtapeOpportunite, string> = {
  [EtapeOpportunite.PROSPECTION]: 'Prospection',
  [EtapeOpportunite.QUALIFICATION]: 'Qualification',
  [EtapeOpportunite.PROPOSITION]: 'Proposition',
  [EtapeOpportunite.NEGOCIATION]: 'Négociation',
  [EtapeOpportunite.GAGNEE]: 'Gagnée',
  [EtapeOpportunite.PERDUE]: 'Perdue',
};

/** Valorisation du stock (TF-025). */
export interface StockValorisationLigne {
  articleId: number;
  designation: string;
  stockActuel: number;
  cmup: number;
  valeur: number;
  co2Kg?: number | null;
  enRupture: boolean;
}
export interface StockValorisation {
  lignes: StockValorisationLigne[];
  totalValeur: number;
  totalCo2Kg: number;
}

/** Synthèse assistant dirigeant (TF-027). */
export interface AssistantSynthese {
  synthese: string;
  genereParIa: boolean;
  metriques: Record<string, any>;
}

/** Assistant commercial — brouillon de devis (TF-026). */
export interface AssistantDevisLigne {
  articleId: number;
  designation: string;
  quantite: number;
  prixUnitaireHT: number;
}
export interface AssistantDevis {
  lignes: AssistantDevisLigne[];
  totalHTEstime: number;
  commentaire: string;
  genereParIa: boolean;
}

/** Rapprochement bancaire assisté (TF-006). */
export interface LigneReleve {
  date?: string | null;
  libelle: string;
  montant: number;
  rapproche: boolean;
  paiementId?: number | null;
  paiementReference?: string | null;
  paiementDate?: string | null;
}
export interface RapprochementAssiste {
  lignes: LigneReleve[];
  nbRapproches: number;
  nbNonRapproches: number;
  totalReleve: number;
}

/** Recommandation carbone (TF-023). */
export interface RecommandationCarbone {
  type: 'SOURCING' | 'DONNEES_MANQUANTES' | 'CATALOGUE';
  priorite: 'HAUTE' | 'MOYENNE' | 'BASSE';
  titre: string;
  detail: string;
  articleId?: number | null;
  gainIntensite?: number | null;
  impactCoutPct?: number | null;
}

/** Cockpit carbone (TF-021/022). */
export interface CarboneContributeur {
  id: number;
  libelle: string;
  co2Kg: number;
  revenuHT: number;
  intensite: number | null;
}

export interface CarboneScoreFournisseur {
  id: number;
  raisonSociale: string;
  co2Kg: number;
  montantAchatHT: number;
  intensite: number | null;
}

export interface CockpitCarbone {
  totalCo2Ventes: number;
  totalCo2Achats: number;
  totalCo2Transport?: number;
  caHT: number;
  intensiteParEuro: number | null;
  topProduits: CarboneContributeur[];
  fournisseurs: CarboneScoreFournisseur[];
}

/** Prévision de trésorerie (TF-013). */
export interface TresoreriePeriode {
  periode: string;
  encaissements: number;
  decaissements: number;
  net: number;
  soldeProjete: number;
}

export interface Tresorerie {
  soldeOuverture: number;
  periodes: TresoreriePeriode[];
}

/** Alerte de pilotage (TF-019). */
export interface Alerte {
  type: 'IMPAYE' | 'RUPTURE_STOCK' | 'OPPORTUNITE' | 'LITIGE';
  severite: 'INFO' | 'ATTENTION' | 'CRITIQUE';
  message: string;
  nombre: number;
  lien: string;
}

/** Balance âgée des impayés (TF-010). */
export interface BalanceAgeeLigne {
  clientId: number;
  clientRaisonSociale: string;
  nonEchu: number;
  b1a30: number;
  b31a60: number;
  b61a90: number;
  plus90: number;
  total: number;
  litige: boolean;
}

export interface BalanceAgee {
  lignes: BalanceAgeeLigne[];
  totaux: Omit<BalanceAgeeLigne, 'clientId' | 'clientRaisonSociale' | 'litige'>;
}

/** Qualité de données — scanner d'anomalies (TF-029 → TF-034). */
export type DataQualitySeverity = 'CRITIQUE' | 'IMPORTANT' | 'MINEUR';

export interface DataQualityIssue {
  code: string;
  severite: DataQualitySeverity;
  module: string;
  entite: string;
  entiteId: number;
  libelle: string;
  detail: string;
  lien: string;
}

export interface DataQualitySummary {
  total: number;
  critiques: number;
  importants: number;
  mineurs: number;
  scoreSante: number;
  parModule: Record<string, number>;
  parCode: Record<string, number>;
  couvertureFacteurCarbone: number;
}

export interface DataQualityReport {
  summary: DataQualitySummary;
  issues: DataQualityIssue[];
}

/** Fiscalité — contrôle TVA (TF-039) et calendrier fiscal (TF-040). */
export interface AnomalieTva {
  documentId: number;
  numero: string;
  designation: string;
  baseHT: number;
  taux: string;
  tvaConstatee: number;
  tvaAttendue: number;
  ecart: number;
}

export interface ControleTva {
  mois: number;
  annee: number;
  tvaCollecteeDocuments: number;
  tvaCollecteeComptabilisee: number;
  ecartCollectee: number;
  tvaDeductibleDocuments: number;
  tvaDeductibleComptabilisee: number;
  ecartDeductible: number;
  tvaNetteAPayer: number;
  coherent: boolean;
  anomaliesLignes: AnomalieTva[];
}

export interface EcheanceFiscale {
  code: string;
  libelle: string;
  dateLimite: string;
  joursRestants: number;
  statut: 'URGENT' | 'PROCHE' | 'A_VENIR';
}

/** État récapitulatif annuel des retenues à la source (TF-041). */
export interface RetenueClient {
  clientId: number;
  clientNom: string;
  matriculeFiscal: string | null;
  montantRetenue: number;
  nbFactures: number;
}

export interface EtatRetenues {
  annee: number;
  total: number;
  lignes: RetenueClient[];
}

/** Recommandations commerciales RFM / next-best-offer (TF-079). */
export interface RecommandationClient {
  clientId: number;
  segment: string;
  potentiel: string;
  recenceJours: number;
  frequence: number;
  monetaire: number;
  actions: string[];
}

/** Objectif / budget annuel (TF-075). */
export interface ObjectifAnnuel {
  annee: number;
  caObjectif: number | null;
  margeObjectif: number | null;
}

/** Activité CRM (TF-078). */
export type TypeActivite = 'APPEL' | 'RDV' | 'TACHE' | 'EMAIL' | 'NOTE';

export interface Activite {
  id: number;
  clientId: number | null;
  type: TypeActivite;
  titre: string;
  description: string | null;
  dateActivite: string;
  terminee: boolean;
}

/** Moteur de validations / approbations (TF-047 → TF-051). */
export type ApprovalType =
  | 'REMISE_DEVIS' | 'FACTURE_MONTANT' | 'AVOIR'
  | 'PAIEMENT_SORTANT' | 'FACTURE_ACHAT' | 'PAIE';
export type ApprovalStatut = 'EN_ATTENTE' | 'APPROUVE' | 'REFUSE';
export type RoleApprobateur = 'ADMIN' | 'COMPTABLE' | 'COMMERCIAL' | 'PLATFORM_ADMIN';

export interface ApprovalRule {
  id: number;
  type: ApprovalType;
  typeLibelle: string;
  pourcentage: boolean;
  seuil: number | null;
  roleApprobateur: RoleApprobateur;
  actif: boolean;
}

export interface ApprovalRequest {
  id: number;
  type: ApprovalType;
  typeLibelle: string;
  objetType: string;
  objetId: number | null;
  libelle: string;
  montant: number | null;
  statut: ApprovalStatut;
  roleApprobateur: RoleApprobateur | null;
  demandeurId: number | null;
  decideurId: number | null;
  motifDecision: string | null;
  createdAt: string;
  decidedAt: string | null;
}

export interface WorkflowSummary {
  enAttente: number;
}

/** Gouvernance carbone (TF-035 → TF-038). */
export interface CarbonGovernanceSummary {
  articlesActifs: number;
  articlesAvecFacteur: number;
  articlesSansFacteur: number;
  couvertureFacteur: number;
  totalCo2Fige: number;
  documentsEstimes: number;
}

export interface CarbonCategoryFactor {
  id: number;
  categorie: string;
  facteurMonetaire: number;
}

export interface ArticleACompleter {
  id: number;
  reference: string;
  designation: string;
  categorie: string | null;
}

/** Portail client (TF-042 → TF-046). */
export interface PortalContext {
  societe: string;
  client: string;
}

export interface PortalDocument {
  id: number;
  numero: string;
  type: string;
  statut: string;
  statutPaiement: string;
  dateDocument: string;
  dateEcheance: string | null;
  totalTTC: number;
  netAPayer: number;
  montantPaye: number;
  resteDu: number;
  totalCo2: number;
  accepterPossible: boolean;
  payable: boolean;
  paymentUrl: string | null;
}

export interface PortalTokenInfo {
  id: number;
  token: string;
  lien: string;
  actif: boolean;
  expiresAt: string | null;
}

/** Portail fournisseur (TF-052 → TF-054). */
export interface SupplierContext {
  societe: string;
  fournisseur: string;
  noteQualite: number | null;
  transportCo2ParCommande: number | null;
}

export interface SupplierOrderLine {
  designation: string;
  quantite: number;
  prixUnitaire: number;
  montantHT: number;
  articleId: number | null;
  emissionFactor: number | null;
}

export interface SupplierOrder {
  id: number;
  numero: string;
  dateDocument: string;
  statut: string;
  totalHT: number;
  totalTTC: number;
  delaiConfirmeJours: number | null;
  lignes: SupplierOrderLine[];
}

/** Recouvrement — paliers de relance (TF-056) et promesses de paiement (TF-057). */
export type TonRelance = 'COURTOIS' | 'FERME' | 'MISE_EN_DEMEURE';

export interface RelancePalier {
  id: number;
  palier: number;
  joursApresEcheance: number;
  ton: TonRelance;
}

export interface PromessePaiement {
  id: number;
  documentId: number;
  documentNumero: string;
  clientNom: string | null;
  datePromesse: string;
  montant: number | null;
  statut: 'EN_ATTENTE' | 'TENUE' | 'NON_TENUE';
  note: string | null;
}

/** Comparateur fournisseur multi-critères (TF-020). */
export interface OffreFournisseur {
  fournisseurId: number;
  fournisseurRaisonSociale: string;
  prefere: boolean;
  prixAchat?: number | null;
  delaiJours?: number | null;
  noteQualite?: number | null;
  intensiteCo2?: number | null;
  scorePrix: number;
  scoreDelai: number;
  scoreQualite: number;
  scoreCo2: number;
  scoreGlobal: number;
}

export interface ComparateurFournisseur {
  articleId: number;
  articleDesignation: string;
  offres: OffreFournisseur[];
}

/** Fiche produit 360° (TF-016). */
export interface Produit360 {
  articleId: number;
  designation: string;
  reference?: string;
  quantiteVendue: number;
  caHT: number;
  nbFactures: number;
  derniereVente?: string | null;
  cmup: number;
  coutHT: number;
  marge: number;
  tauxMargePct: number | null;
  stockActuel: number;
  stockMinimum?: number | null;
  enRupture: boolean;
  emissionFactor?: number | null;
  co2VenduKg?: number | null;
  co2StockKg?: number | null;
}

/** Grille tarifaire / remise client (TF-024). */
export type TypeTarif = 'PRIX_NET' | 'REMISE_POURCENTAGE';
export interface TarifClient {
  id: number;
  clientId: number;
  clientRaisonSociale?: string;
  articleId?: number | null;
  articleDesignation?: string | null;
  type: TypeTarif;
  valeur: number;
  actif?: boolean;
}

/** Score client (TF-018). */
export interface ScoreClient {
  clientId: number;
  clientRaisonSociale: string;
  score: number;
  categorie: 'A' | 'B' | 'C';
  caTotalHT: number;
  encours: number;
  nbFactures: number;
  nbEnRetard: number;
  derniereFacture?: string | null;
  depassementPlafond: boolean;
}

/** Contact multiple d'un tiers (TF-004). */
export interface Contact {
  id: number;
  clientId?: number | null;
  fournisseurId?: number | null;
  nom: string;
  fonction?: string;
  email?: string;
  telephone?: string;
  principal?: boolean;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Opportunite {
  id: number;
  clientId?: number | null;
  clientRaisonSociale?: string | null;
  prospectNom?: string | null;
  titre: string;
  description?: string;
  montantEstime: number;
  probabilite: number;
  etape: EtapeOpportunite;
  montantPondere: number;
  dateCloturePrev?: string | null;
  motifPerte?: string | null;
  source?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface PipelineColonne {
  etape: EtapeOpportunite;
  nombre: number;
  totalEstime: number;
  totalPondere: number;
  opportunites: Opportunite[];
}

export interface Pipeline {
  colonnes: PipelineColonne[];
  totalOuvertEstime: number;
  previsionPonderee: number;
  nbOuvertes: number;
}

export const STATUT_DOCUMENT_LABELS: Record<StatutDocument, string> = {
  [StatutDocument.BROUILLON]: 'Brouillon',
  [StatutDocument.ENVOYE]: 'Envoyé',
  [StatutDocument.ACCEPTE]: 'Accepté',
  [StatutDocument.REFUSE]: 'Refusé',
  [StatutDocument.EXPIRE]: 'Expiré',
  [StatutDocument.VALIDE]: 'Validé',
  [StatutDocument.ANNULE]: 'Annulé',
};
